
import { useState, useEffect } from "react";
import { 
  CompanyRegisterProvider, 
  CompanySearchResult, 
  CompanyDetails,
  companyRegisters,
  isProviderEnabled
} from "@/lib/api/companyRegister";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface CompanyRegisterPreferences {
  defaultProviderId?: string;
  bolagsverket_api_key?: string;
  companieshouse_api_key?: string;
}

export function useCompanyRegister() {
  const [isSearching, setIsSearching] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [searchResults, setSearchResults] = useState<CompanySearchResult[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<CompanyDetails | null>(null);
  const [preferences, setPreferences] = useState<CompanyRegisterPreferences | null>(null);
  const [isLoadingPreferences, setIsLoadingPreferences] = useState(true);
  const [providers, setProviders] = useState<CompanyRegisterProvider[]>([]);
  const [isLoadingProviders, setIsLoadingProviders] = useState(true);
  const { user } = useAuth();
  
  useEffect(() => {
    async function loadProviders() {
      setIsLoadingProviders(true);
      try {
        const enabledProviders: CompanyRegisterProvider[] = [];
        
        for (const provider of companyRegisters) {
          const isEnabled = await isProviderEnabled(provider.id);
          if (isEnabled) {
            enabledProviders.push(provider);
          }
        }
        
        console.log("Loaded enabled providers:", enabledProviders.map(p => p.id));
        setProviders(enabledProviders);
      } catch (error) {
        console.error("Error loading company register providers:", error);
        setProviders(companyRegisters);
      } finally {
        setIsLoadingProviders(false);
      }
    }
    
    loadProviders();
  }, []);
  
  useEffect(() => {
    async function loadPreferences() {
      if (!user) {
        setIsLoadingPreferences(false);
        return;
      }
      
      setIsLoadingPreferences(true);
      try {
        const { data, error } = await supabase
          .from('user_preferences')
          .select('company_register_provider, bolagsverket_api_key, companieshouse_api_key')
          .eq('user_id', user.id)
          .single();
        
        if (error) {
          if (error.code !== 'PGRST116') {
            console.error("Error loading company register preferences:", error);
          }
          setPreferences(null);
        } else if (data) {
          let defaultProviderId = data.company_register_provider;
          if (defaultProviderId) {
            const isEnabled = await isProviderEnabled(defaultProviderId);
            if (!isEnabled) {
              defaultProviderId = undefined;
            }
          }
          
          console.log("Loaded user preferences with default provider:", defaultProviderId);
          
          setPreferences({
            defaultProviderId,
            bolagsverket_api_key: data.bolagsverket_api_key || undefined,
            companieshouse_api_key: data.companieshouse_api_key || undefined
          });
        }
      } catch (error) {
        console.error("Error loading company register preferences:", error);
      } finally {
        setIsLoadingPreferences(false);
      }
    }
    
    loadPreferences();
  }, [user]);
  
  const searchCompany = async (query: string, providerId?: string) => {
    if (query.length < 2) {
      console.log("Query too short:", query);
      return [];
    }
    
    setIsSearching(true);
    try {
      let results: CompanySearchResult[] = [];
      
      if (providerId) {
        console.log(`Searching for "${query}" using specific provider:`, providerId);
        const isEnabled = await isProviderEnabled(providerId);
        if (!isEnabled) {
          toast.error(`The selected provider ${providerId} is not enabled`);
          return [];
        }
        
        const provider = companyRegisters.find(p => p.id === providerId);
        if (provider) {
          results = await provider.search(query);
          console.log(`Search results using provider ${providerId}:`, results.length);
        } else {
          console.error(`Provider ${providerId} not found`);
        }
      } else {
        const defaultProviderId = preferences?.defaultProviderId;
        console.log(`Searching for "${query}" using default provider:`, defaultProviderId);
        
        if (defaultProviderId) {
          const isEnabled = await isProviderEnabled(defaultProviderId);
          if (isEnabled) {
            const provider = companyRegisters.find(p => p.id === defaultProviderId);
            if (provider) {
              console.log(`Searching using default provider: ${defaultProviderId}`);
              results = await provider.search(query);
              console.log(`Search results:`, results.length);
            } else {
              console.error(`Default provider ${defaultProviderId} not found`);
            }
          } else {
            console.warn(`Default provider ${defaultProviderId} is not enabled, falling back`);
            if (providers.length > 0) {
              console.log(`Falling back to first provider: ${providers[0].id}`);
              results = await providers[0].search(query);
            }
          }
        } else {
          console.log(`No default provider, using all available providers (${providers.length})`);
          const searchPromises = providers.map(provider => provider.search(query));
          const providerResults = await Promise.all(searchPromises);
          results = providerResults.flat();
        }
      }
      
      setSearchResults(results);
      return results;
    } catch (error) {
      console.error("Error searching for companies:", error);
      return [];
    } finally {
      setIsSearching(false);
    }
  };
  
  const getCompanyDetails = async (id: string, providerId: string) => {
    setIsFetching(true);
    try {
      const isEnabled = await isProviderEnabled(providerId);
      if (!isEnabled) {
        throw new Error(`Provider ${providerId} is not enabled`);
      }
      
      const provider = companyRegisters.find(p => p.id === providerId);
      if (!provider) {
        throw new Error(`Provider ${providerId} not found`);
      }
      
      const details = await provider.getCompanyDetails(id);
      setSelectedCompany(details);
      return details;
    } finally {
      setIsFetching(false);
    }
  };
  
  const updatePreferences = async (newPreferences: Partial<CompanyRegisterPreferences>) => {
    if (!user) {
      console.error("Cannot update preferences: User not authenticated");
      return false;
    }
    
    if (newPreferences.defaultProviderId) {
      const isEnabled = await isProviderEnabled(newPreferences.defaultProviderId);
      if (!isEnabled) {
        toast.error(`Cannot set ${newPreferences.defaultProviderId} as default provider because it is disabled by the administrator`);
        return false;
      }
    }
    
    try {
      const { data: existingPrefs } = await supabase
        .from('user_preferences')
        .select('id')
        .eq('user_id', user.id)
        .single();
      
      if (existingPrefs) {
        const { error } = await supabase
          .from('user_preferences')
          .update({ 
            company_register_provider: newPreferences.defaultProviderId,
            bolagsverket_api_key: newPreferences.bolagsverket_api_key,
            companieshouse_api_key: newPreferences.companieshouse_api_key,
            updated_at: new Date().toISOString()
          })
          .eq('user_id', user.id);
        
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('user_preferences')
          .insert({ 
            user_id: user.id,
            company_register_provider: newPreferences.defaultProviderId,
            bolagsverket_api_key: newPreferences.bolagsverket_api_key,
            companieshouse_api_key: newPreferences.companieshouse_api_key
          });
        
        if (error) throw error;
      }
      
      setPreferences(prev => ({
        ...prev,
        ...newPreferences
      }));
      
      return true;
    } catch (error) {
      console.error("Error updating company register preferences:", error);
      return false;
    }
  };
  
  const reset = () => {
    setSearchResults([]);
    setSelectedCompany(null);
  };
  
  return {
    searchCompany,
    getCompanyDetails,
    updatePreferences,
    reset,
    providers,
    searchResults,
    selectedCompany,
    preferences,
    isSearching,
    isFetching,
    isLoadingPreferences,
    isLoadingProviders
  };
}
